import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { Sparkles, Send, CheckCircle, Mail, AlertCircle } from 'lucide-react';

/**
 * Detect the base URL where the static files are hosted.
 * Works on WordPress by finding the <script> tag src and extracting the base path.
 * Falls back to current origin if not found.
 */
function getBaseUrl() {
  const scripts = document.querySelectorAll('script[src*="main."]');
  for (const s of scripts) {
    const src = s.getAttribute('src') || '';
    const idx = src.indexOf('static/js/main.');
    if (idx !== -1) return src.substring(0, idx);
  }
  return window.location.origin + '/';
}

const QuizSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const baseUrl = getBaseUrl();
      const submitUrl = baseUrl + 'ff-submit.php';

      const res = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
        }),
      });

      const data = await res.json();

      if (data.success) {
        setIsSubmitted(true);
      } else {
        setSubmitError(data.message || 'Une erreur est survenue.');
      }
    } catch (err) {
      // Network or CORS error — show message anyway if on different domain (preview)
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="quiz"
      data-testid="quiz-section"
      className="relative py-24 md:py-32 bg-[#F5F5F4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-2 md:order-1"
          >
            <div className="relative">
              <div className="absolute -inset-4 border border-[#D4AF37]/30 rounded-3xl" />
              <div className="absolute -inset-8 border border-[#D4AF37]/10 rounded-3xl" />
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1756672987836-4749b7c0270c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxkb2xwaGluJTIwb2NlYW4lMjBzdW5saWdodCUyMGJlYXV0aWZ1bCUyMHBlYWNlZnVsJTIwYmx1ZXxlbnwwfHx8fDE3NzM4NDM3MDh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Dauphin lumineux dans l'océan"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                  data-testid="quiz-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09]/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-2">
                    Quiz Gratuit
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                    Quel Dauphin<br />
                    <span className="italic text-[#D4AF37]">te guide</span> en ce moment ?
                  </h3>
                </div>
              </div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-4 -right-4 bg-[#D4AF37] text-[#1C1917] rounded-full px-4 py-2 shadow-lg"
              >
                <span className="font-bold text-sm uppercase tracking-wider">2 min</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Form Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 md:order-2"
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="text-[#D4AF37]" size={20} />
              <span className="text-[#D4AF37] uppercase tracking-widest text-sm font-medium">
                Découvrez votre guide
              </span>
            </div>

            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#1C1917] leading-tight mb-6">
              Quel <span className="italic text-[#D4AF37]">Dauphin Allié</span><br />
              t'accompagne ?
            </h2>

            <p className="text-[#44403C] text-lg leading-relaxed mb-8">
              Un dauphin veille sur toi, prêt à t'accompagner dans ta transformation.
              Il t'aide à dépasser tes défis, activer tes potentiels et harmoniser
              ton énergie pour franchir ton prochain palier d'évolution.
            </p>

            <div className="space-y-3 mb-8 text-[#44403C]">
              <p className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                Fais le quiz en 2 minutes
              </p>
              <p className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                Reçois ton guide personnalisé
              </p>
              <p className="flex items-center gap-3">
                <span className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                Découvre ton Dauphin allié
              </p>
            </div>

            {/* Form Card */}
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-[#D4AF37]/10">
              <AnimatePresence mode="wait">
                {isSubmitted ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="text-center py-6"
                    data-testid="quiz-success-message"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"
                    >
                      <CheckCircle className="text-[#D4AF37]" size={40} strokeWidth={1.5} />
                    </motion.div>
                    <h3 className="font-display text-2xl text-[#1C1917] mb-3">
                      C'est envoyé !
                    </h3>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <Mail className="text-[#D4AF37]" size={18} />
                      <p className="text-[#44403C] text-lg font-medium">
                        Consulte tes mails
                      </p>
                    </div>
                    <p className="text-[#A8A29E] text-sm max-w-xs mx-auto">
                      Tu vas recevoir un e-mail avec le lien pour découvrir ton Dauphin Guide. Pense à vérifier tes spams.
                    </p>
                    <button
                      onClick={() => {
                        setIsSubmitted(false);
                        setSubmitError('');
                        setFormData({ firstName: '', lastName: '', email: '' });
                      }}
                      className="mt-6 text-[#D4AF37] text-sm underline underline-offset-4 hover:text-[#AA8C2C] transition-colors"
                      data-testid="quiz-retry-btn"
                    >
                      Pas reçu ? Réessayer
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    onSubmit={handleSubmit}
                    className="space-y-5"
                    data-testid="quiz-form"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <input
                          type="text"
                          placeholder="Prénom"
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                          required
                          data-testid="quiz-input-firstname"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Nom"
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                          required
                          data-testid="quiz-input-lastname"
                        />
                      </div>
                    </div>
                    <div>
                      <input
                        type="email"
                        placeholder="Adresse e-mail"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-[#1C1917] placeholder:text-[#A8A29E] transition-colors"
                        required
                        data-testid="quiz-input-email"
                      />
                    </div>
                    {submitError && (
                      <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="quiz-error">
                        <AlertCircle size={16} />
                        {submitError}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="quiz-submit-btn"
                      className="w-full mt-4 bg-gradient-to-r from-[#D4AF37] to-[#AA8C2C] text-white rounded-full px-8 py-4 uppercase tracking-widest text-sm font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          Je fais le Quiz
                          <Send size={16} />
                        </>
                      )}
                    </button>
                    <p className="text-xs text-[#A8A29E] text-center mt-3">
                      Tu recevras en prime mes articles et pépites hebdomadaires
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QuizSection;
